import React, { useState } from 'react';
import {
  Alert,
  Button,
  Flex,
  Heading,
  Text,
  TextField,
  View,
} from '@aws-amplify/ui-react';
import Loadingmini from '../components/loadingmini';
import { api3Post } from '../api/api3Client';

function partyLabel(side) {
  if (!side) return '—';
  const name = [side.nameFirst, side.nameLast].filter(Boolean).join(' ') || 'Unknown';
  return `${name} · ${side.phone || 'no phone'} · ${side.email || 'no email'}`;
}

/**
 * Support/admin combine flow only (SMS link to surviving phone).
 * No phone edit — escalate single-party phone change to Derek/Ryan.
 */
export default function CombineAccounts() {
  const [keepPartyId, setKeepPartyId] = useState('');
  const [dropPartyId, setDropPartyId] = useState('');
  const [inviteToken, setInviteToken] = useState('');
  const [preview, setPreview] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function runPreview() {
    setLoading(true);
    setError('');
    setPreview(null);
    setChallenge(null);
    setResult(null);
    try {
      const res = await api3Post('/parties/combine/preview', {
        keepPartyId: Number(keepPartyId),
        dropPartyId: Number(dropPartyId),
        phoneSource: 'drop',
      });
      setPreview(res);
    } catch (e) {
      setError(e.message || String(e));
    }
    setLoading(false);
  }

  async function sendSms() {
    setLoading(true);
    setError('');
    setChallenge(null);
    setResult(null);
    try {
      const res = await api3Post('/parties/combine/challenge', {
        keepPartyId: Number(keepPartyId),
        dropPartyId: Number(dropPartyId),
        phoneSource: 'drop',
        cognitoSource: 'drop',
      });
      setChallenge(res);
    } catch (e) {
      setError(e.message || String(e));
    }
    setLoading(false);
  }

  async function confirmCombine() {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await api3Post('/parties/combine', {
        keepPartyId: Number(keepPartyId),
        dropPartyId: Number(dropPartyId),
        phoneSource: 'drop',
        cognitoSource: 'drop',
        confirmPhrase: 'COMBINE',
        inviteToken: (inviteToken || '').trim(),
      });
      setResult(res);
    } catch (e) {
      setError(e.message || String(e));
    }
    setLoading(false);
  }

  return (
    <View padding="1rem" maxWidth="52rem">
      <Heading level={3}>Combine duplicate accounts</Heading>

      <Alert variation="info" isDismissible={false} marginTop="1rem">
        <Text>
          Use this when the same driver has two parties (often after a new phone signup).
          Keep the real profile; the new phone stays as the login number. The driver must
          tap the SMS link (or give you the code). There is no “edit phone” control —
          for a single-party phone change, escalate to Ryan and Derek.
        </Text>
      </Alert>

      <View marginTop="1rem">
        <Heading level={5}>Steps</Heading>
        <Text as="ol" style={{ paddingLeft: '1.25rem', marginTop: '0.5rem' }}>
          <li>Find keep (real / older) and drop (Unknown / new phone) party ids on Renters.</li>
          <li>Preview — soft warnings are OK (Unknown name, missing license).</li>
          <li>Send SMS — driver taps the link on the surviving (usually new) phone.</li>
          <li>
            If they tapped the link, you are done. Otherwise enter the 6-digit code and confirm.
          </li>
          <li>Tell the driver to sign in with that phone. Reply in Slack with keep id + phone.</li>
        </Text>
      </View>

      <Flex direction="row" gap="0.75rem" wrap="wrap" marginTop="1.25rem" alignItems="flex-end">
        <TextField
          label="Keep party id (real profile)"
          value={keepPartyId}
          onChange={(e) => setKeepPartyId(e.target.value.trim())}
          width="12rem"
        />
        <TextField
          label="Drop party id (duplicate)"
          value={dropPartyId}
          onChange={(e) => setDropPartyId(e.target.value.trim())}
          width="12rem"
        />
        <Button
          onClick={runPreview}
          isLoading={loading}
          isDisabled={!keepPartyId || !dropPartyId}
        >
          1. Preview
        </Button>
      </Flex>

      {error ? (
        <Alert variation="error" isDismissible={false} marginTop="1rem">
          {error}
        </Alert>
      ) : null}

      {loading && !preview && !challenge ? <Loadingmini /> : null}

      {preview ? (
        <View marginTop="1.25rem" padding="1rem" backgroundColor="var(--amplify-colors-background-secondary)">
          <Heading level={5}>Preview</Heading>
          <Text>
            <strong>Keep:</strong> {partyLabel(preview.keep)} (id {preview.keepPartyId})
          </Text>
          <Text>
            <strong>Drop:</strong> {partyLabel(preview.drop)} (id {preview.dropPartyId})
          </Text>
          <Text>
            <strong>Surviving login phone (SMS goes here):</strong>{' '}
            {preview.survivingPhone || '—'}
          </Text>
          {(preview.warnings || []).length > 0 ? (
            <Text marginTop="0.5rem">
              Soft warnings (do not block): {(preview.warnings || []).join(', ')}
            </Text>
          ) : (
            <Text marginTop="0.5rem">No soft warnings.</Text>
          )}
          <Button marginTop="0.75rem" onClick={sendSms} isLoading={loading}>
            2. Send combine SMS
          </Button>
        </View>
      ) : null}

      {challenge ? (
        <View marginTop="1.25rem">
          <Alert variation="success" isDismissible={false}>
            {challenge.message || 'SMS sent.'} Sent to {challenge.otpSentTo || preview?.survivingPhone}.
            Prefer: driver taps the link in the text. Or enter the code below if they read it to you.
          </Alert>
          <Flex direction="row" gap="0.75rem" wrap="wrap" marginTop="0.75rem" alignItems="flex-end">
            <TextField
              label="SMS code (only if they did not tap the link)"
              value={inviteToken}
              onChange={(e) => setInviteToken(e.target.value.trim())}
              width="16rem"
            />
            <Button
              variation="primary"
              onClick={confirmCombine}
              isLoading={loading}
              isDisabled={!inviteToken}
            >
              3. Confirm combine
            </Button>
          </Flex>
        </View>
      ) : null}

      {result ? (
        <Alert variation="success" isDismissible={false} marginTop="1rem">
          Combined. Keep party {result.keepPartyId || keepPartyId}. Tell the driver to sign in
          with {preview?.survivingPhone || 'the surviving phone'}.
        </Alert>
      ) : null}
    </View>
  );
}
